import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, Globe, Check, ChevronDown } from 'lucide-react';
import { Button } from './ui/button';
import { useTranslation } from 'react-i18next';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSub,
  DropdownMenuSubTrigger,
  DropdownMenuSubContent,
  DropdownMenuLabel,
} from "./ui/dropdown-menu";

const Header = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const location = useLocation();
  const { t, i18n } = useTranslation();

  const isActive = (path) => {
    if (path === '/') return location.pathname === '/';
    return location.pathname.startsWith(path);
  };



  const navLinks = [
    { path: '/', label: t('nav.home') },
    {
      label: t('nav.consultancy'),
      children: [
        { path: '/consultancy/wireline-qualification', label: t('nav.wireline_qualification') },
        { path: '/consultancy/wireline-operation', label: t('nav.wireline_operation') },
        { path: '/consultancy/manufacturing', label: t('nav.manufacturing') },
        { path: '/consultancy/ai-technology', label: t('nav.ai_technology') },
      ]
    },
    {
      label: t('nav.sales_rental'),
      children: [
        { path: '/products', label: t('nav.oilfield_products') },
      ]
    },
    {
      label: t('nav.resources'),
      children: [
        { path: '/resources/manpower', label: t('nav.talented_manpower') },
      ]
    },
    { path: '/about', label: t('nav.about') },
    { path: '/contact', label: t('nav.contact') },
  ];

  const isParentActive = (children) => {
    return children.some(child => child.path && isActive(child.path));
  };

  return (
    <header className={`sticky top-0 z-50 bg-white h-20 ${isMenuOpen ? '' : 'shadow-md'}`}>
      <div className="max-w-7xl mx-auto px-6 h-full">
        <div className="flex items-center justify-between h-full">
          
          {/* Logo */}
          <Link to="/" className="flex items-center gap-3 group">
            <div className="w-12 h-12 bg-[#0A1F44] rounded-lg flex items-center justify-center group-hover:bg-[#FF6A00] transition-colors duration-300">
              <span className="text-white font-bold text-xl">HH</span>
            </div>
            <div className="hidden md:block">
              <div className="font-bold text-[#0A1F44] text-lg leading-tight">
                {t('nav.brand')}
              </div>
              <div className="text-xs text-gray-600">{t('nav.tagline')}</div>
            </div>
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden lg:flex items-center gap-8">
            {navLinks.map((link, index) => (
              link.children ? (
                <DropdownMenu key={index}>
                  <DropdownMenuTrigger className={`group flex items-center gap-1 text-sm font-semibold transition-colors duration-300 outline-none data-[state=open]:text-[#FF6A00] ${
                    isParentActive(link.children) ? 'text-[#FF6A00]' : 'text-gray-700 hover:text-[#FF6A00]'
                  }`}>
                    {link.label}
                    <ChevronDown className="h-4 w-4 transition-transform duration-300 group-data-[state=open]:rotate-180" />
                  </DropdownMenuTrigger>
                  <DropdownMenuContent 
                    align="start" 
                    className="w-64 bg-white/95 backdrop-blur-sm border border-gray-100 shadow-xl rounded-xl p-2 animate-in fade-in-0 zoom-in-95 data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=closed]:zoom-out-95 duration-200"
                    sideOffset={8}
                  >
                    {link.children.map((child, childIndex) => {
                      if (child.type === 'label') {
                        return (
                          <DropdownMenuLabel key={childIndex} className="px-4 py-2 text-xs font-bold text-gray-500 uppercase tracking-wider">
                            {child.label}
                          </DropdownMenuLabel>
                        );
                      }
                      
                      if (child.children) {
                        return (
                          <DropdownMenuSub key={child.label}>
                            <DropdownMenuSubTrigger className="w-full flex items-center justify-between px-4 py-3 rounded-lg text-sm font-medium text-gray-600 hover:text-[#FF6A00] hover:bg-orange-50 hover:pl-6 transition-all duration-200 cursor-pointer">
                              {child.label}
                            </DropdownMenuSubTrigger>
                            <DropdownMenuSubContent className="w-64 bg-white/95 backdrop-blur-sm border border-gray-100 shadow-xl rounded-xl p-2">
                              {child.children.map((subChild) => (
                                <DropdownMenuItem key={subChild.path} asChild className="focus:bg-transparent">
                                  <Link
                                    to={subChild.path}
                                    className={`w-full block px-4 py-3 rounded-lg text-sm font-medium transition-all duration-200 cursor-pointer ${
                                      isActive(subChild.path) 
                                        ? 'text-[#FF6A00] bg-orange-50' 
                                        : 'text-gray-600 hover:text-[#FF6A00] hover:bg-orange-50 hover:pl-6'
                                    }`}
                                  >
                                    {subChild.label}
                                  </Link>
                                </DropdownMenuItem>
                              ))}
                            </DropdownMenuSubContent>
                          </DropdownMenuSub>
                        );
                      }

                      return (
                        <DropdownMenuItem key={child.path} asChild className="focus:bg-transparent">
                          <Link
                            to={child.path}
                            className={`w-full block px-4 py-3 rounded-lg text-sm font-medium transition-all duration-200 cursor-pointer ${
                              isActive(child.path) 
                                ? 'text-[#FF6A00] bg-orange-50' 
                                : 'text-gray-600 hover:text-[#FF6A00] hover:bg-orange-50 hover:pl-6'
                            }`}
                          >
                            {child.label}
                          </Link>
                        </DropdownMenuItem>
                      );
                    })}
                  </DropdownMenuContent>
                </DropdownMenu>
              ) : (
                <Link
                  key={link.path}
                  to={link.path}
                  className={`text-sm font-semibold relative group transition-colors duration-300 ${
                    isActive(link.path)
                      ? 'text-[#FF6A00]'
                      : 'text-gray-700 hover:text-[#FF6A00]'
                  }`}
                >
                  {link.label}
                  <span
                    className={`absolute -bottom-2 left-0 h-0.5 bg-[#FF6A00] transition-all duration-300 ${
                      isActive(link.path) ? 'w-full' : 'w-0 group-hover:w-full'
                    }`}
                  ></span>
                </Link>
              )
            ))}
          </nav>

          {/* CTA + Language Switch - Desktop */}
          <div className="hidden lg:flex items-center gap-4">
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  variant="ghost"
                  className="group flex items-center gap-2 px-3 py-2 text-gray-700 hover:text-[#FF6A00] hover:bg-orange-50 rounded-full transition-all duration-300 focus-visible:ring-0 focus-visible:ring-offset-0 data-[state=open]:bg-orange-50 data-[state=open]:text-[#FF6A00]"
                  aria-label="Switch Language"
                >
                  <Globe className="h-4 w-4 opacity-80 group-hover:rotate-12 transition-transform duration-300" />
                  <span className="font-semibold text-sm">
                    {i18n.language === 'zh' ? '中文' : 'EN'}
                  </span>
                  <ChevronDown className="h-3 w-3 opacity-50 transition-transform duration-300 group-data-[state=open]:rotate-180" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent 
                align="end" 
                className="w-[140px] bg-white/95 backdrop-blur-sm border border-gray-100 shadow-xl rounded-xl p-2 animate-in fade-in-0 zoom-in-95 duration-200"
              >
                <DropdownMenuItem 
                  onClick={() => i18n.changeLanguage('en')}
                  className={`cursor-pointer flex items-center justify-between px-3 py-2.5 rounded-lg transition-colors duration-200 ${
                    i18n.language === 'en' 
                      ? 'bg-orange-50 text-[#FF6A00] font-medium' 
                      : 'text-gray-600 hover:bg-gray-50 hover:text-[#FF6A00]'
                  }`}
                >
                  <span className="flex items-center gap-2">
                    <span>English</span>
                  </span>
                  {i18n.language === 'en' && <Check className="h-3.5 w-3.5" />}
                </DropdownMenuItem>
                <DropdownMenuItem 
                  onClick={() => i18n.changeLanguage('zh')}
                  className={`cursor-pointer flex items-center justify-between px-3 py-2.5 rounded-lg transition-colors duration-200 ${
                    i18n.language === 'zh' 
                      ? 'bg-orange-50 text-[#FF6A00] font-medium' 
                      : 'text-gray-600 hover:bg-gray-50 hover:text-[#FF6A00]'
                  }`}
                >
                  <span className="flex items-center gap-2">
                    <span>中文</span>
                  </span>
                  {i18n.language === 'zh' && <Check className="h-3.5 w-3.5" />}
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>

            <Link to="/contact">
              <Button className="bg-[#FF6A00] hover:bg-[#E55F00] text-white transition-all duration-300 transform hover:scale-105">
                {t('nav.contactSales')}
              </Button>
            </Link>
          </div>

          {/* Mobile Menu Button + Language Switch */}
          <div className="lg:hidden flex items-center gap-3">
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  variant="ghost"
                  className="group flex items-center gap-2 px-3 py-2 text-gray-700 hover:text-[#FF6A00] hover:bg-orange-50 rounded-full transition-all duration-300 focus-visible:ring-0 focus-visible:ring-offset-0 data-[state=open]:bg-orange-50 data-[state=open]:text-[#FF6A00]"
                  aria-label="Switch Language"
                >
                  <Globe className="h-4 w-4 opacity-80 group-hover:rotate-12 transition-transform duration-300" />
                  <span className="font-semibold text-sm">
                    {i18n.language === 'zh' ? '中文' : 'EN'}
                  </span>
                  <ChevronDown className="h-3 w-3 opacity-50 transition-transform duration-300 group-data-[state=open]:rotate-180" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent 
                align="start" 
                className="w-[140px] bg-white/95 backdrop-blur-sm border border-gray-100 shadow-xl rounded-xl p-2 animate-in fade-in-0 zoom-in-95 duration-200"
              >
                <DropdownMenuItem 
                  onClick={() => i18n.changeLanguage('en')}
                  className={`cursor-pointer flex items-center justify-between px-3 py-2.5 rounded-lg transition-colors duration-200 ${
                    i18n.language === 'en' 
                      ? 'bg-orange-50 text-[#FF6A00] font-medium' 
                      : 'text-gray-600 hover:bg-gray-50 hover:text-[#FF6A00]'
                  }`}
                >
                  <span className="flex items-center gap-2">
                    <span>English</span>
                  </span>
                  {i18n.language === 'en' && <Check className="h-3.5 w-3.5" />}
                </DropdownMenuItem>
                <DropdownMenuItem 
                  onClick={() => i18n.changeLanguage('zh')}
                  className={`cursor-pointer flex items-center justify-between px-3 py-2.5 rounded-lg transition-colors duration-200 ${
                    i18n.language === 'zh' 
                      ? 'bg-orange-50 text-[#FF6A00] font-medium' 
                      : 'text-gray-600 hover:bg-gray-50 hover:text-[#FF6A00]'
                  }`}
                >
                  <span className="flex items-center gap-2">
                    <span>中文</span>
                  </span>
                  {i18n.language === 'zh' && <Check className="h-3.5 w-3.5" />}
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>

            <button
              onClick={() => setIsMenuOpen(!isMenuOpen)}
              className="p-2 text-gray-700 hover:text-[#FF6A00] transition-colors"
              aria-label="Toggle menu"
            >
              {isMenuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
            </button>
          </div>
        </div>

        {/* Mobile Navigation */}
        {isMenuOpen && (
          <nav className="lg:hidden fixed top-[79px] left-0 w-full h-[calc(100vh-79px)] bg-white shadow-lg py-6 px-6 space-y-6 animate-in slide-in-from-top-5 fade-in duration-300 overflow-y-auto z-40 border-t border-gray-100">
            {navLinks.map((link, index) => (
              link.children ? (
                <div key={index} className="space-y-2">
                  <div className="px-4 py-2 text-sm font-bold text-gray-900">
                    {link.label}
                  </div>
                  <div className="pl-4 space-y-1">
                    {link.children.map((child, childIndex) => {
                      if (child.type === 'label') {
                        return (
                          <div key={childIndex} className="px-4 py-2 text-xs font-bold text-gray-500 uppercase tracking-wider">
                            {child.label}
                          </div>
                        );
                      }
                      return (
                        <Link
                          key={child.path}
                          to={child.path}
                          onClick={() => setIsMenuOpen(false)}
                          className={`block py-3 px-4 rounded-lg transition-all duration-300 text-base ${
                            isActive(child.path)
                              ? 'bg-[#FF6A00] text-white font-semibold'
                              : 'text-gray-600 hover:bg-gray-100 font-medium'
                          }`}
                        >
                          {child.label}
                        </Link>
                      );
                    })}
                  </div>
                </div>
              ) : (
                <Link
                  key={link.path}
                  to={link.path}
                  onClick={() => setIsMenuOpen(false)}
                  className={`block py-3 px-4 rounded-lg transition-all duration-300 text-base ${
                    isActive(link.path)
                      ? 'bg-[#FF6A00] text-white font-semibold'
                      : 'text-gray-700 hover:bg-gray-100 font-medium'
                  }`}
                >
                  {link.label}
                </Link>
              )
            ))}

            <div className="pt-4 flex flex-col gap-4">
              <Link to="/contact" onClick={() => setIsMenuOpen(false)}>
                <Button className="w-full bg-[#FF6A00] hover:bg-[#E55F00] text-white">
                  {t('nav.contactSales')}
                </Button>
              </Link>
            </div>
          </nav>
        )}
      </div>
    </header>
  );
};

export default Header;
